/**
 * Journey `capturedData` names one field per document, with a 1-based suffix and no suffix at
 * all on the first: `country` / `country2` / `country3`, `idType` / `idType2` / `idType3`.
 *
 * one-journey-backend builds the same names in `helper.util.getFieldName`. The two have to agree
 * exactly — a screen that wrote `country2` where the service reads `country3` would capture a
 * document against one country and verify it against another.
 *
 * @param {string} field - Base field name, e.g. 'country'
 * @param {number} documentIndex - 0-based document index, as the journey screens track it
 * @returns {string} The capturedData key for that document
 */
export function getDocumentFieldName(field, documentIndex) {
    return documentIndex > 0 ? `${field}${documentIndex + 1}` : field;
}

/**
 * A captured country is stored either as a plain code or as the whole country object, depending
 * on which screen wrote it. Callers only ever want the code.
 */
const toCountryCode = (country) =>
    (country && typeof country === 'object' ? country.value : country) || '';

/**
 * The country the applicant selected for a given document.
 *
 * Documents after the first carry a country of their own only when the flow gives its documents
 * different country lists (IDX-677). Every other journey — including every journey started
 * before per-document country existed — falls back to `country`, which has always held the
 * single flow-wide selection, so nothing needs backfilling.
 *
 * Mirrors `getCapturedCountry` in one-journey-backend.
 *
 * @param {Object} capturedData - The journey's capturedData
 * @param {number} documentIndex - 0-based document index
 * @returns {string} The country code for that document, or '' when none was captured
 */
export function getCapturedCountry(capturedData, documentIndex) {
    const forThisDocument = capturedData?.[getDocumentFieldName('country', documentIndex)];
    return toCountryCode(forThisDocument) || toCountryCode(capturedData?.country);
}

/**
 * The country a capture screen should render for a given document.
 *
 * Three tiers, in this order: what the applicant selected for THIS document, what the journey was
 * prefilled with for THIS document, and only then the flow-wide `country`.
 *
 * The flow-wide value has to come last. It is the back-compat tier — every journey started before
 * per-document country existed holds its single selection there — so reaching for it before the
 * per-document prefill answers a question about document 2 with document 1's country. That is
 * exactly what happens on the prefill path: document 2's country screen is skipped, so nothing
 * ever writes `country2`, and a `country` left behind by document 1 would shadow the
 * `userInfo.id.country2` the operator chose in the Create Journey modal.
 *
 * @param {Object} journey - The journey, as the capture screens hold it
 * @param {number} documentIndex - 0-based document index
 * @returns {string} The country code for that document, or '' when none is known
 */
export function getDocumentCountry(journey, documentIndex) {
    const field = getDocumentFieldName('country', documentIndex);
    return (
        toCountryCode(journey?.capturedData?.[field]) ||
        toCountryCode(journey?.userInfo?.id?.[field]) ||
        toCountryCode(journey?.capturedData?.country)
    );
}

/**
 * The document type the applicant chose for a given document.
 *
 * Unlike the country there is no fallback to the first document's value. `idType2` has existed for
 * as long as journeys have captured two documents, so a missing one means this document has not
 * been chosen yet — and answering with the previous document's type is exactly how a passport in
 * slot one came to decide that slot two needed no back side.
 *
 * @param {Object} journey - The journey, as the capture screens hold it
 * @param {number} documentIndex - 0-based document index
 * @returns {string} The document type for that document, or '' when none is known
 */
export function getDocumentIdType(journey, documentIndex) {
    const field = getDocumentFieldName('idType', documentIndex);
    return journey?.capturedData?.[field] || journey?.userInfo?.id?.[field] || '';
}
